import { useState, useEffect } from "react";
import { MessageCircle, Check } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SERVICES, BUSINESS } from "@/data/cybernet";

export const Catalog = () => {
  const [active, setActive] = useState(SERVICES[0].id);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail && SERVICES.find((s) => s.id === e.detail)) {
        setActive(e.detail);
      }
    };
    window.addEventListener("cn:catalog:focus", handler);
    return () => window.removeEventListener("cn:catalog:focus", handler);
  }, []);

  const buildWA = (itemTitle) =>
    `https://wa.me/${BUSINESS.primaryWhatsapp}?text=${encodeURIComponent(
      `Hola CYBERNET, quiero información / cotización sobre: ${itemTitle}`
    )}`;

  return (
    <section
      id="catalogo"
      data-testid="catalog-section"
      className="bg-[#FAFAFA] py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="uppercase text-xs font-bold tracking-[0.25em] text-[#E10600] mb-4">
              — Catálogo
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#0A0A0A] max-w-2xl">
              Explora todo lo que ofrecemos.
            </h2>
          </div>
          <p className="text-[#52525B] max-w-md text-base leading-relaxed">
            Elige una categoría y descubre todos los servicios y productos
            disponibles. Solicita tu cotización directo por WhatsApp.
          </p>
        </div>

        <Tabs value={active} onValueChange={setActive} className="w-full">
          <TabsList
            data-testid="catalog-tabs"
            className="w-full h-auto flex-wrap justify-start gap-2 bg-transparent p-0 mb-10"
          >
            {SERVICES.map((s) => (
              <TabsTrigger
                key={s.id}
                value={s.id}
                data-testid={`catalog-tab-${s.id}`}
                className="px-5 py-3 text-sm font-semibold border-2 border-[#E4E4E7] bg-white text-[#0A0A0A] data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white data-[state=active]:border-[#0A0A0A] hover:border-[#E10600] rounded-sm transition-all"
              >
                <span className="text-[#E10600] mr-2 data-[state=active]:text-white">
                  {s.number}
                </span>
                {s.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {SERVICES.map((s) => (
            <TabsContent
              key={s.id}
              value={s.id}
              data-testid={`catalog-content-${s.id}`}
              className="mt-0"
            >
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5">
                  <div className="relative aspect-[4/5] max-h-[560px] overflow-hidden rounded-sm">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                      <h3 className="font-display font-black text-2xl text-white">
                        {s.title}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <div className="grid gap-3">
                    {s.items.map((item, i) => (
                      <div
                        key={i}
                        data-testid={`catalog-item-${s.id}-${i}`}
                        className="group flex items-start justify-between gap-4 bg-white border border-[#E4E4E7] p-5 hover:border-[#E10600] hover:shadow-md transition-all rounded-sm"
                      >
                        <div className="flex items-start gap-4 flex-1">
                          <div className="mt-1 shrink-0 h-6 w-6 rounded-full bg-[#E10600] flex items-center justify-center">
                            <Check className="h-3.5 w-3.5 text-white" />
                          </div>
                          <div className="text-[#0A0A0A] font-medium leading-relaxed">
                            {item}
                          </div>
                        </div>
                        <a
                          href={buildWA(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`catalog-quote-${s.id}-${i}`}
                          className="shrink-0 hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#E10600] opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          Cotizar
                        </a>
                      </div>
                    ))}
                  </div>
                  <a
                    data-testid={`catalog-info-${s.id}`}
                    href={`https://wa.me/${BUSINESS.primaryWhatsapp}?text=${encodeURIComponent(
                      `Hola CYBERNET, quiero información sobre: ${s.title}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 bg-[#E10600] text-white text-sm font-semibold px-6 py-3 rounded-sm hover:bg-[#B30500] transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Solicitar información
                  </a>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
